import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Button, TextField, Alert, CircularProgress, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import stateDistricts from '../../data/stateDistricts';
import categories from '../../data/categories';

function EditItemPage() {
    const { id } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const fetchItem = async () => {
            try {
                const { data } = await axios.get(`/api/admin/items/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Convert extraPoints array back to string for editing
                data.extraPoints = data.extraPoints.join(', ');
                setItem(data);
            } catch (err) {
                setError('Failed to fetch item details.');
            } finally {
                setLoading(false);
            }
        };
        fetchItem();
    }, [id, token]);

    const handleChange = (e) => {
        setItem({ ...item, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const updateData = {
                ...item,
                extraPoints: item.extraPoints.split(',').map(s => s.trim()).filter(Boolean)
            };
            await axios.patch(`/api/admin/items/${id}`, updateData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccess('Item updated successfully!');
            setTimeout(() => navigate('/admin/posts'), 2000);
        } catch (err) {
            setError('Failed to update item.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!item) return <Typography>Item not found.</Typography>;

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>Edit Item: {item.itemName}</Typography>
            {success && <Alert severity="success">{success}</Alert>}
            <form onSubmit={handleSubmit}>
                <TextField name="itemName" label="Item Name" value={item.itemName || ''} fullWidth margin="normal" required onChange={handleChange} />
                <TextField name="description" label="Description" value={item.description || ''} fullWidth margin="normal" multiline rows={4} required onChange={handleChange} />
                <TextField name="extraPoints" label="Identifying Marks" value={item.extraPoints || ''} fullWidth margin="normal" onChange={handleChange} />
                <FormControl fullWidth margin="normal" required>
                    <InputLabel>Category</InputLabel>
                    <Select name="category" value={item.category || ''} label="Category" onChange={handleChange}>
                        {categories.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
                    </Select>
                </FormControl>
                {/* Add other fields as needed */}
                <Button type="submit" variant="contained" disabled={loading} sx={{ mt: 2 }}>
                    {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                </Button>
                <Button onClick={() => navigate('/admin/posts')} sx={{ mt: 2, ml: 1 }}>Cancel</Button>
            </form>
        </Paper>
    );
}

export default EditItemPage;
